/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import 'react-virtualized/styles.css';

import * as React from 'react';
import { memo, useMemo } from 'react';
import { useQuery } from 'react-query';

import { Stack } from '@mui/material';
import Box from '@mui/material/Box';

import { AdviceSeverities, AdviceTypes, ClusterStates } from 'shared';

import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { fetchAdvice, fetchDeepthinkerLatestRuntime, fetchDismissedAdvice } from '../../../../utils/queryFns';
import { AdviceChart } from './components/AdviceChart';
import { AdviceSeveritySummary } from './components/AdviceSeveritySummary';
import { AdviceTable } from './components/AdviceTable';

type IProps = {
  identificationData: UpdateClusterDetailsParameterIdentification;
  status: ClusterStates | undefined;
};

export const AdviceTab = memo<IProps>(props => {
  const { identificationData, status } = props;

  const adviceQuery = useQuery([fetchAdvice.key, { ...identificationData }], fetchAdvice.fn);
  const advice = useMemo(
    () =>
      adviceQuery.data?.data?.payload.sort(
        (a, b) =>
          AdviceSeverities.indexOf(b.severity) - AdviceSeverities.indexOf(a.severity) ||
          AdviceTypes.indexOf(a.type) - AdviceTypes.indexOf(b.type) ||
          (a?.object && b?.object ? a.object.localeCompare(b.object) : 0) ||
          (a?.subObject && b?.subObject ? a.subObject.localeCompare(b.subObject) : 0) ||
          a.info.localeCompare(b.info),
      ) || [],
    [adviceQuery.data],
  );

  const dismissedAdviceQuery = useQuery([fetchDismissedAdvice.key, { ...identificationData }], fetchDismissedAdvice.fn);
  const dismissedAdvice = useMemo(() => dismissedAdviceQuery.data?.data?.payload || [], [dismissedAdviceQuery.data]);

  const deepthinkerLatestRuntimeQuery = useQuery(
    [fetchDeepthinkerLatestRuntime.key, { ...identificationData }],
    fetchDeepthinkerLatestRuntime.fn,
    { refetchInterval: 10_000 },
  );
  const deepthinkerLatestRuntime = useMemo(
    () => deepthinkerLatestRuntimeQuery.data?.data.payload || {},
    [deepthinkerLatestRuntimeQuery.data],
  );

  return (
    <Stack>
      <Stack direction="row" width="100%" height="45vh" mb={1}>
        <Stack width="40%" justifyContent="center">
          <AdviceSeveritySummary
            identificationData={identificationData}
            advice={advice}
            dismissedAdvice={dismissedAdvice}
            latestRuntime={deepthinkerLatestRuntime}
            status={status}
          />
        </Stack>
        <Box width="60%">
          <AdviceChart advice={advice} />
        </Box>
      </Stack>

      <AdviceTable identificationData={identificationData} advice={advice} />
    </Stack>
  );
});
